// Situation Modal - Goal Re-collection After Test 8
// Shows user their situation/goal options for better analytics segmentation

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation as getTranslationHelper } from '../utils/translationHelpers';
import './SituationModal.css';

type SituationGoal = 'first-time' | 'failed' | 'exam-soon' | null;

interface SituationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (goal: SituationGoal) => void;
}

const GOALS = {
  'first-time': {
    en: 'Taking it for the first time',
    nl: 'Voor het eerst',
    ar: 'أول مرة'
  },
  'failed': {
    en: 'I\'ve failed before',
    nl: 'Ik ben eerder gezakt',
    ar: 'لقد فشلت من قبل'
  },
  'exam-soon': {
    en: 'My exam is soon',
    nl: 'Mijn examen is binnenkort',
    ar: 'امتحاني قريب'
  }
} as const;

export const SituationModal: React.FC<SituationModalProps> = ({ isOpen, onClose, onSave }) => {
  const { t_nested, currentLanguage } = useLanguage();

  const handleSelect = (goal: SituationGoal) => {
    if (goal) {
      onSave(goal);
      onClose();
    }
  };

  const handleSkip = () => {
    onSave(null);
    onClose();
  };

  if (!isOpen) return null;

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as keyof typeof GOALS['first-time'];

  return (
    <div className="situation-modal-overlay" onClick={handleSkip}>
      <div className="situation-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="situation-modal-title">
          {getTranslationHelper(t_nested, 'situation.title', 'What\'s your goal?')}
        </h2>
        <p className="situation-modal-subtitle">
          {getTranslationHelper(t_nested, 'situation.subtitle', 'Help us personalize your learning')}
        </p>

        <div className="situation-goals-container">
          {(Object.keys(GOALS) as Array<keyof typeof GOALS>).map((goalKey) => (
            <button
              key={goalKey}
              className="situation-goal-button"
              onClick={() => handleSelect(goalKey)}
            >
              <span className="situation-goal-text">
                {GOALS[goalKey][lang]}
              </span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="situation-modal-skip"
          onClick={handleSkip}
        >
          {getTranslationHelper(t_nested, 'situation.skip', 'Skip for now')}
        </button>
      </div>
    </div>
  );
};
